<template>

    <div :class="['field', { 'error': hasError, 'animation': labelAnimation, 'dropdown-form': hasImages }]">
        <label class="label"
               :for="id"
               v-if="!labelAnimation && label">
            {{ labelText }}
        </label>

        <div :class="['control is-expanded', { 'has-icons-right': icon || hasError }]" v-click-outside="handleOutsideClick">
			<span class="select is-fullwidth">
                <select :id="id"
                        v-if="hasImages === true"
                        :disabled="disabled"
                        @click="openDropdownMenu"
                        :class="[
                            {'select-invisible': typeof $attrs.invisible !== 'undefined'},
                            {'is-danger': hasError},
                            gClass,
                            size,
                        ]"/>

				<select :id="id"
                        v-else-if="!multiple"
                        v-model="data[name]"
                        v-bind="$attrs"
                        :disabled="disabled"
                        :class="[
                            {'select-invisible': typeof $attrs.invisible !== 'undefined'},
                            {'is-danger': hasError},
                            gClass,
                            size,
                        ]">

					<!-- Render ungrouped options -->
					<option v-for="option in groupedOptions.ungrouped"
                            :value="option['value']"
                            :key="option['value']"
                            v-bind="$attrs"
                            :disabled="typeof option.disabled !== 'undefined'">{{ option['name'] }}
					</option>

					<!-- Render grouped options -->
					<optgroup v-for="(options, groupName) in groupedOptions.groups"
                              :label="groupName"
                              :key="groupName">
						<option v-for="option in options"
                                :value="option['value']"
                                :key="option['value']"
                                v-bind="$attrs"
                                :disabled="typeof option.disabled !== 'undefined'">{{ option['name'] }}
						</option>
					</optgroup>

				</select>
                 <tree-select
                     :id="id"
                     v-else-if="multiple"
                     v-model="data[name]"
                     v-bind="$attrs"
                     :disabled="disabled"
                     :class="[
                            {'select-invisible': typeof $attrs.invisible !== 'undefined'},
                            {'is-danger': hasError},
                            gClass,
                            size,
                        ]"
                     :multiple="true"
                     :options="computedMultipleOptions"
                     :placeholder="$t('g-select.placeholder')"
                     :clearable="false"
                     value-consists-of="LEAF_PRIORITY"
                     :limit="0"
                     :limitText="(count) => $t('g-select.multiple_selected', {count})"
                     :noResultsText="$t('g-select.no_results')"/>
			</span>

            <i class="icon is-right" :class="[size]" v-if="!hasError && !!icon">{{ icon }}</i>
            <i class="icon is-right is-danger" :class="[size]" v-if="hasError">error</i>
        </div>

        <ul class="dropdown-wrapper" v-if="hasImages === true && imageDropdownVisible === true">
            <li v-for="option in optionsWithImages"
                :key="option[valuekey]"
                @click="handleDropdownClick(option[valuekey])"
                class="dropdown-item">
                <div class="dropdown-container">
                    <img :src="option.image.src"
                         :alt="option.name"
                         :style="{
                             'width': option.image.width ?? '1.563rem',
                             'height': option.image.height ?? '1.563rem'
                         }"
                         :class="{ 'dropdown-active': data[name] === option.value }"
                         class="dropdown-image">
                    <span class="dropdown-text">{{ option.name }}</span>
                </div>
            </li>
        </ul>

        <div class="hover-area"
             v-if="hasError"
             @mouseover="showErrorMessage"
             @mouseleave="hideErrorMessage">
            <div class="message-box">
                <div class="error-message" v-show="errorMessageVisible">
                    {{ errorMessage }}
                </div>
            </div>
        </div>
        <label :class="['label', { 'active': isSelectActive || isInputActive }]"
               :for="id"
               v-if="labelAnimation">
            {{ labelText }}
        </label>

        <div class="dropdown-label" v-if="hasImages === true && activeImageOption">
            {{ activeImageOption.name }}
        </div>
    </div>
</template>

<script>
import InputHelper from '../../utils/InputHelper'
import ClickOutside from '../ClickOutsideDirective'

export default InputHelper.extend({
    directives: {
        'click-outside': ClickOutside
    },
    props:   {
        valuekey:  { default: 'value' },
        namekey:   { default: 'name' },
        options:   { required: true },
        multiple:  { default: false },
        size:      '',
        hasImages: { default: false, type: Boolean }
    },
    data () {
        return {
            imageDropdownVisible: false
        }
    },
    methods: {
        handleOutsideClick() {
            if (this.hasImages === false || this.imageDropdownVisible === false) {
                return;
            }

            this.imageDropdownVisible = false;
        },
        openDropdownMenu(e) {
            e.preventDefault()

            if (this.hasImages === false) {
                return;
            }

            this.imageDropdownVisible = !this.imageDropdownVisible
        },

        handleDropdownClick(value) {
            this.data[this.name] = value;
            this.imageDropdownVisible = false;
            this.$emit('input', value);
        },
    },
    created () {
        this.$watch(`data.${this.name}`, value => {
            this.$emit('input', value)
        })
    },
    computed: {
        activeImageOption() {
            if (this.hasImages === false) {
                return;
            }

            const activeOption = this.options.find(option => option[this.valuekey] === this.data[this.name])
            if (!activeOption) {
                return;
            }

            return activeOption
        },
        optionsWithImages () {
            return this.options.filter(option => option.image && option.image.src)
        },
        computedOptions() {
            if(this.options === 'trueFalse') {
                return [
                    {name: this.$t('g-form.defaults.yes'), value: 1},
                    {name: this.$t('g-form.defaults.no'), value: 0},
                ]
            }

            if ('string' === typeof this.options[0]) {
                const newOptions = []
                this.options.forEach(option => {
                    newOptions.push({
                        name:  option,
                        value: option,
                    })
                })
                return newOptions
            }

            return this.options.map(option => {
                const result = {
                    name:  option[this.namekey],
                    value: option[this.valuekey],
                }

                // Preserve group if it exists
                if (option.group !== undefined) {
                    result.group = option.group
                }

                return result
            })
        },
        computedMultipleOptions() {
            // Map to id and label
            return this.computedOptions.map(option => {
                return {
                    id: option['value'],
                    label: option['name'],
                }
            })
        },
        groupedOptions() {
            // Organize options by group
            const groups = {};
            const ungrouped = [];

            // Check if computedOptions exists and is an array
            if (Array.isArray(this.computedOptions)) {
                this.computedOptions.forEach(option => {
                    if (option.group) {
                        if (!groups[option.group]) {
                            groups[option.group] = [];
                        }
                        groups[option.group].push(option);
                    } else {
                        ungrouped.push(option);
                    }
                });
            }

            return { groups, ungrouped };
        },
        isSelectActive() {
            // Check if an option with value null exists
            if (typeof this.computedOptions === 'undefined') {
                return false;
            }

            if(this.data[this[this.namekey]] !== null) {
                return true;
            }

            const nullOptionExists = this.computedOptions.filter(option => {
                return option[this.valuekey] === null;
            }).length > 0

            return nullOptionExists && this.data[this[this.namekey]] === null;
        }
    },
})

</script>
